import { JoinRequestStatus } from "@superblocksteam/shared";

// Persisted-dismissal key for the Join Org Gate. We persist across reloads because the
// credit-card gate (and other downstream flows like checkout) can trigger full page
// navigations, which would otherwise remount the gate with `skipped = false` and re-show
// "Request sent" on every reload until an admin approves or denies.
//
// We include userId, status, and org id in the key so a *meaningful* state change
// (PENDING → DENIED, or a different org now matches) re-shows the card — dismissing the
// pending screen should not silently swallow a later "declined" screen.
export const buildAckKey = (
  userId: string | undefined,
  status: JoinRequestStatus | null | undefined,
  orgId: string | undefined,
): string =>
  `joinOrgGate:acked:${userId ?? "anon"}:${status ?? "none"}:${orgId ?? "none"}`;

// `requestSent` flips to true immediately after POST /v1/join-requests, but the
// Redux-backed `joinRequestStatus` keeps its prior value until the next /me refetch. If we
// build the ackKey from the stale value, the key we *write* on Continue won't match the key
// we *read* after the CC-gate's full-page reload (which sees status=PENDING), and the card
// re-appears on every reload.
//
// When the local "just sent" signal is set and the server's last-known status is one of the
// "no active request" states (null / CANCELLED), treat it as authoritative PENDING — covers
// both first-time requests and re-requests after a cancellation, so the UI flips to the
// "Request sent" card without waiting on /me. DENIED is NOT overridden: if the admin denies
// while the page is open and /me refetches, we must key under DENIED so the admin's decision
// isn't hidden by a stale local PENDING dismissal.
export const resolveEffectiveStatus = (
  requestSent: boolean,
  serverStatus: JoinRequestStatus | null | undefined,
): JoinRequestStatus | null | undefined => {
  if (!requestSent) return serverStatus;
  if (!serverStatus || serverStatus === JoinRequestStatus.CANCELLED) {
    return JoinRequestStatus.PENDING;
  }
  return serverStatus;
};

export const readAck = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};

export const writeAck = (key: string): void => {
  try {
    window.localStorage.setItem(key, "true");
  } catch {
    // ignore (private mode, quota, etc.) — we'll just re-show until state changes
  }
};
